// ============================================================
// Viashift — Reconciliation data layer (Phase 3, Epic 3B)
// ============================================================
// Read + write helpers backing the /reconciliation pages. Sits
// on top of:
//   - reconciliation_cycles table
//   - reconciliation_items table
//   - start_reconciliation_cycle RPC (3B.1)
//   - submit_reconciliation_cycle RPC (3B.3)
//
// Walk-through item edits (set submitted_count, toggle skip)
// route through direct UPDATE on reconciliation_items with RLS
// narrowing to in-progress cycles. Per-item writes don't go
// through an RPC because there's no math involved — just
// authoritative state for the parent cycle to read at submit
// time.
//
// Note on types: `database.types.ts` was regenerated 2026-06-29 and
// now includes reconciliation_cycles and reconciliation_items, so the
// client is fully typed (no `as any` boundary cast). The setItemSkipped
// update is typed against the generated table Update type; the submit
// RPC's jsonb return crosses the Json/SubmitResult seam via a narrow
// cast at that one point.
// ============================================================

import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

// Typed client. reconciliation_cycles, reconciliation_items, and the
// start/submit RPCs are in the generated database.types as of the
// 2026-06-29 regen, so no `as any` boundary cast is needed.
const sb = supabase;

// ---- Row shapes ---------------------------------------------------

export type CycleStatus = 'in_progress' | 'submitted' | 'cancelled';

export interface ReconciliationCycleRow {
  id: string;
  clinic_id: string;
  status: CycleStatus;
  started_at: string;
  started_by: string;
  submitted_at: string | null;
  submitted_by: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  notes: string | null;
  totals_in_scope: number | null;
  totals_counted: number | null;
  totals_skipped: number | null;
  totals_with_delta: number | null;
  created_at: string;
  updated_at: string;
}

export interface ReconciliationItemRow {
  id: string;
  clinic_id: string;
  cycle_id: string;
  product_id: string;
  baseline_count: number;
  submitted_count: number | null;
  skipped: boolean;
  created_at: string;
  updated_at: string;
}

/** Item joined with the live product fields needed for the
 *  walk-through UI. Live count is read at fetch time so the
 *  reviewer sees current state (scans / receipts since cycle
 *  start are reflected). baseline_count is the snapshot. */
export interface ReconciliationItemWithProduct extends ReconciliationItemRow {
  product: {
    id: string;
    name: string;
    uom: string;
    par: number | null;
    count: number;  // live count, NOT the baseline snapshot
    category_id: string;
  };
}

// ---- Reads --------------------------------------------------------

/** Return the in-progress cycle for the clinic, or null. Used
 *  by the Inventory page's affordance switch (Start vs Resume)
 *  and by the reconciliation landing. */
export async function getInProgressCycle(
  clinicId: string,
): Promise<ReconciliationCycleRow | null> {
  const { data, error } = await sb
    .from('reconciliation_cycles')
    .select('*')
    .eq('clinic_id', clinicId)
    .eq('status', 'in_progress')
    .maybeSingle();

  if (error) throw error;
  return (data as ReconciliationCycleRow | null) ?? null;
}

/** History list — submitted + cancelled, newest first.
 *  Backs the recent-history strip on the landing page and
 *  (eventually) the full 3B.4 history page. */
export async function listHistoryCycles(
  clinicId: string,
  limit = 20,
): Promise<ReconciliationCycleRow[]> {
  const { data, error } = await sb
    .from('reconciliation_cycles')
    .select('*')
    .eq('clinic_id', clinicId)
    .in('status', ['submitted', 'cancelled'])
    .order('started_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data as ReconciliationCycleRow[]) ?? [];
}

/** Fetch a single cycle by id. */
export async function getCycle(
  clinicId: string,
  cycleId: string,
): Promise<ReconciliationCycleRow | null> {
  const { data, error } = await sb
    .from('reconciliation_cycles')
    .select('*')
    .eq('clinic_id', clinicId)
    .eq('id', cycleId)
    .maybeSingle();

  if (error) throw error;
  return (data as ReconciliationCycleRow | null) ?? null;
}

/** Fetch all items for a cycle joined with the live product
 *  data needed for the walk-through. Sort: essentials handled
 *  by the in-scope filter at start time, so the products
 *  arriving here are already the in-scope set. Within that,
 *  sort alphabetically — same convention as InventoryPage. */
export async function listCycleItems(
  clinicId: string,
  cycleId: string,
): Promise<ReconciliationItemWithProduct[]> {
  const { data, error } = await sb
    .from('reconciliation_items')
    .select(`
      id, clinic_id, cycle_id, product_id, baseline_count,
      submitted_count, skipped, created_at, updated_at,
      products!inner ( id, name, uom, par, count, category_id )
    `)
    .eq('clinic_id', clinicId)
    .eq('cycle_id', cycleId);

  if (error) throw error;

  type Joined = ReconciliationItemRow & {
    products: ReconciliationItemWithProduct['product'];
  };

  const rows = (data as Joined[]) ?? [];
  const mapped: ReconciliationItemWithProduct[] = rows.map((r) => ({
    id: r.id,
    clinic_id: r.clinic_id,
    cycle_id: r.cycle_id,
    product_id: r.product_id,
    baseline_count: r.baseline_count,
    submitted_count: r.submitted_count,
    skipped: r.skipped,
    created_at: r.created_at,
    updated_at: r.updated_at,
    product: r.products,
  }));

  mapped.sort((a, b) => a.product.name.localeCompare(b.product.name));
  return mapped;
}

// ---- Writes -------------------------------------------------------

/** Start a new cycle. Returns the new cycle id.
 *  Refuses if another in_progress cycle exists (PostgreSQL
 *  error P0001 surfaces as a .code/.message pair). */
export async function startCycle(clinicId: string): Promise<string> {
  const { data, error } = await sb.rpc('start_reconciliation_cycle', {
    p_clinic_id: clinicId,
  });

  if (error) throw error;
  return data as string;
}

/** Set the submitted count for a single item. Pass null to
 *  clear (reverts to "not yet counted"). Clears the skipped
 *  flag implicitly — submitting a count means the item is
 *  not skipped. */
export async function setItemCount(
  clinicId: string,
  itemId: string,
  count: number | null,
): Promise<void> {
  const { error } = await sb
    .from('reconciliation_items')
    .update({
      submitted_count: count,
      skipped: false,
    })
    .eq('clinic_id', clinicId)
    .eq('id', itemId);

  if (error) throw error;
}

/** Toggle the skipped flag. Skipping clears any submitted_count
 *  (the table CHECK enforces mutual exclusion). Unskipping
 *  leaves submitted_count null — the user has to enter a count
 *  fresh. */
export async function setItemSkipped(
  clinicId: string,
  itemId: string,
  skipped: boolean,
): Promise<void> {
  const update: Database['public']['Tables']['reconciliation_items']['Update'] =
    { skipped };
  if (skipped) update.submitted_count = null;

  const { error } = await sb
    .from('reconciliation_items')
    .update(update)
    .eq('clinic_id', clinicId)
    .eq('id', itemId);

  if (error) throw error;
}

/** Cancel an in-progress cycle. Direct UPDATE with RLS gating;
 *  the policy only allows updates to status='in_progress' rows
 *  so this is naturally idempotent against an already-terminal
 *  cycle (would no-op). */
export async function cancelCycle(
  clinicId: string,
  cycleId: string,
): Promise<void> {
  const { error } = await sb
    .from('reconciliation_cycles')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
    })
    .eq('clinic_id', clinicId)
    .eq('id', cycleId);

  if (error) throw error;
}

export interface SubmitResult {
  cycle_id: string;
  events_written: number;
  total_in_scope: number;
  total_counted: number;
  total_skipped: number;
  total_with_delta: number;
  submitted_at: string;
}

/** Submit (finalize) the cycle. RPC computes deltas, writes
 *  inferred_usage events, stamps cached totals, and flips
 *  status. Returns the totals payload for the success banner. */
export async function submitCycle(
  clinicId: string,
  cycleId: string,
  notes: string | null,
): Promise<SubmitResult> {
  // exactOptionalPropertyTypes is on: an optional prop won't accept an
  // explicit `undefined`, so omit p_notes entirely when there's no note
  // rather than passing undefined.
  const rpcArgs = {
    p_clinic_id: clinicId,
    p_cycle_id: cycleId,
    ...(notes != null ? { p_notes: notes } : {}),
  };
  const { data, error } = await sb.rpc('submit_reconciliation_cycle', rpcArgs);

  if (error) throw error;
  // RPC returns jsonb → typed as Json; actual shape is SubmitResult.
  return data as unknown as SubmitResult;
}

// ---- Display helpers ----------------------------------------------

/** Format a cycle timestamp range for the history list. */
export function formatCycleRange(cycle: ReconciliationCycleRow): string {
  const started = new Date(cycle.started_at);
  const ended =
    cycle.submitted_at ?? cycle.cancelled_at
      ? new Date(cycle.submitted_at ?? cycle.cancelled_at!)
      : null;

  const startedStr = started.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  if (!ended) return `Started ${startedStr}`;

  const sameDay =
    started.toDateString() === ended.toDateString();
  if (sameDay) return startedStr;

  const endedStr = ended.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${startedStr} → ${endedStr}`;
}

/** Format a relative time like "12m ago", "3h ago", or absolute
 *  date for older. Mirrors ReviewPage's formatRelativeTime to
 *  keep the visual register consistent. */
export function formatRelativeTime(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const diffMin = diffMs / 60_000;
  const diffHour = diffMin / 60;
  const diffDay = diffHour / 24;
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${Math.floor(diffMin)}m ago`;
  if (diffHour < 24) return `${Math.floor(diffHour)}h ago`;
  if (diffDay < 7) return `${Math.floor(diffDay)}d ago`;
  return then.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Item-level progress derivation. Used by the walk-through
 *  header ("7 of 23 counted · 2 skipped") and by the landing
 *  resume-card. */
export interface CycleProgress {
  inScope: number;
  counted: number;
  skipped: number;
  remaining: number;
}

export function deriveProgress(
  items: readonly ReconciliationItemWithProduct[],
): CycleProgress {
  let counted = 0;
  let skipped = 0;
  for (const it of items) {
    if (it.skipped) skipped++;
    else if (it.submitted_count !== null) counted++;
  }
  const inScope = items.length;
  return {
    inScope,
    counted,
    skipped,
    remaining: inScope - counted - skipped,
  };
}

/** Categorize an item by its current state. Used by row
 *  styling and by the "first uncounted" finder for the
 *  walk-through. */
export type ItemState = 'counted' | 'skipped' | 'pending';

export function itemState(
  item: ReconciliationItemWithProduct,
): ItemState {
  if (item.skipped) return 'skipped';
  if (item.submitted_count !== null) return 'counted';
  return 'pending';
}

/** Live delta the user has currently entered — submitted minus
 *  the live product count. Returns null for skipped or
 *  not-yet-counted items.
 *
 *  This is what's previewed at submit time. The actual write
 *  uses the same math on the server side at the moment of
 *  submit, so a scan between this read and the submit
 *  re-evaluates against the latest count. */
export function previewDelta(
  item: ReconciliationItemWithProduct,
): number | null {
  if (item.skipped || item.submitted_count === null) return null;
  return item.submitted_count - item.product.count;
}
